import React, { useEffect, useId, useMemo, useState } from "react";
import { Button, Typography } from "../../../common";
import { QuickQuestionContent } from "../../../../types/newtypes";
import { CheckCircle } from "lucide-react";
import { useUpdateUserTagMutation } from "../../../../store/api/postgresUserApi";

interface QuickQuestionPreviewProps {
  content: QuickQuestionContent;
  onCompleted?: () => void;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
}

const QuickQuestionPreview: React.FC<QuickQuestionPreviewProps> = ({
  content,
  onCompleted,
  onCardStateChange,
}) => {
  const privacyGuidanceId = useId();
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [textAnswer, setTextAnswer] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveWarning, setSaveWarning] = useState<string | null>(null);
  const [updateUserTag] = useUpdateUserTagMutation();

  useEffect(() => {
    onCardStateChange?.(false, false);
  }, [onCardStateChange]);

  const questionText = content?.question_text || content?.question || "";
  const options = useMemo(
    () =>
      (content?.options || content?.answer_options || []).map((option) =>
        typeof option === "string"
          ? { text: option, tag_value: option }
          : { text: option.text, tag_value: option.tag_value || option.text }
      ),
    [content?.answer_options, content?.options]
  );

  const handleOptionSelect = (index: number) => {
    setSelectedOption(index);
    setTextAnswer(""); // Clear text answer when option is selected
  };

  const handleTextChange = (value: string) => {
    setTextAnswer(value);
    setSelectedOption(null); // Clear option when text is entered
  };

  const handleSubmit = async () => {
    const selected = selectedOption !== null ? options[selectedOption] : null;
    const tagValue = (selected?.tag_value || selected?.text || textAnswer).trim();
    if (!tagValue) return;

    setIsSaving(true);
    setSaveWarning(null);
    if (content?.tag_key) {
      try {
        await updateUserTag({
          tag_key: content.tag_key,
          tag_value: tagValue,
        }).unwrap();
      } catch (e) {
        setSaveWarning(
          "Your answer is saved for this unit, but could not be stored to your profile right now."
        );
      }
    }

    setIsSubmitted(true);
    onCardStateChange?.(true, true);
    onCompleted?.();
    setIsSaving(false);
  };

  return (
    <div className="w-full">
      {/* Slide Title */}
      <Typography variant="h4" weight="bold" className="mb-4">
        {content?.slide_title}
      </Typography>

      {/* Intro Text */}
      <Typography variant="p" className="text-gray-700 mb-6">
        {content?.intro_text}
      </Typography>

      {/* Question */}
      <Typography variant="h5" weight="bold" className="mb-6">
        {questionText}
      </Typography>

      {/* Options or Text Input */}
      {options.length > 0 ? (
        // Multiple choice options
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {options.map((option, index) => (
            <button
              key={index}
              type="button"
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                selectedOption === index
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300"
              } text-left`}
              onClick={() => handleOptionSelect(index)}
            >
              <Typography variant="p" className="text-gray-800">
                {option.text}
              </Typography>
            </button>
          ))}
        </div>
      ) : (
        // Text input for open-ended questions
        <div className="mb-6">
          <textarea
            aria-label={questionText || "Your answer"}
            aria-describedby={privacyGuidanceId}
            value={textAnswer}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder="Share your thoughts..."
            className="w-full p-4 border-2 border-gray-200 rounded-lg resize-none focus:border-blue-500 focus:outline-none transition-colors duration-200"
            rows={4}
          />
          <Typography variant="small" className="text-gray-600 mt-2">
            <span id={privacyGuidanceId}>
              Use made-up details. Do not include real pupil or family information, names or other personal or sensitive details.
            </span>
          </Typography>
        </div>
      )}

      {!isSubmitted ? (
        <>
          {/* Submit Button */}
          <Button
            label="Submit"
            variant="filled"
            size="large"
            onClick={handleSubmit}
            className="mb-6"
            disabled={isSaving || (selectedOption === null && !textAnswer?.trim())}
          />
          {isSaving && (
            <Typography variant="small" className="text-gray-600 mb-4">
              Saving...
            </Typography>
          )}
          {/* Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-sm:mb-4">
            <Typography variant="small" className="text-blue-800">
              This is a reflection question. There are no right or wrong
              answers - use the response that best matches your context.
            </Typography>
          </div>
        </>
      ) : (
        /* Success Message */
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <Typography
            variant="h5"
            weight="bold"
            className="text-green-800 mb-2 inline-flex items-center gap-2"
          >
            <CheckCircle className="w-8 h-8 text-green-600" />
            Thanks for sharing! 🙌
          </Typography>
          <Typography variant="p" className="text-green-700 mb-4">
            Press Next to continue.
          </Typography>
          {saveWarning && (
            <Typography variant="small" className="text-amber-700">
              {saveWarning}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

export default QuickQuestionPreview;
